// SMS: send through SmsManager, read threads and incoming messages through
// the Telephony provider (no SMS role, AGENTS.md §7.1). RCS is unavailable.

import type { SmsThreadSummary } from "@fistos/shared";
import { MessageSquare, RefreshCw, Send } from "lucide-solid";
import { createMemo, createSignal, For, Show } from "solid-js";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { TextField, TextFieldInput, TextFieldLabel, TextFieldTextArea } from "@/components/ui/text-field";
import { now } from "@/lib/clock";
import { ago } from "@/lib/format";
import { live } from "@/lib/live";
import { actions, parse } from "@/lib/proto";
import { OfflineNote, run, Unavailable, type PanelProps } from "./common";

export function MessagesPanel(props: PanelProps) {
  const id = () => props.device.id;
  const [threads, setThreads] = createSignal<SmsThreadSummary[] | null>(null);
  const [to, setTo] = createSignal("");
  const [body, setBody] = createSignal("");
  const [sending, setSending] = createSignal(false);

  const incoming = createMemo(() =>
    (live.events[id()] ?? [])
      .filter((e) => e.kind === "sms")
      .map((e) => ({ at: e.at, m: parse.sms(e.data) }))
      .reverse(),
  );

  const load = async () => {
    const r = await run(id(), actions.listThreads(30));
    if (r?.payload.case === "smsThreads") setThreads(r.payload.value.threads);
  };
  const send = async (e: Event) => {
    e.preventDefault();
    setSending(true);
    const r = await run(id(), actions.sms(to().trim(), body()), "Message sent");
    setSending(false);
    if (r) setBody("");
  };

  return (
    <Show
      when={props.caps?.smsSend !== false || props.caps?.smsReceive !== false}
      fallback={<Unavailable title="SMS is not available">This phone reports no SMS support.</Unavailable>}
    >
      <div class="grid gap-4 *:min-w-0 lg:grid-cols-2">
        <Show when={!props.device.online}>
          <div class="lg:col-span-2">
            <OfflineNote device={props.device} />
          </div>
        </Show>
        <div class="grid content-start gap-4">
          <Card class="gap-4 py-5">
            <CardHeader class="px-5">
              <CardTitle class="text-base">New message</CardTitle>
              <CardDescription>
                Messages to numbers that are not contacts wait for your approval. RCS chats are not reachable.
              </CardDescription>
            </CardHeader>
            <CardContent class="px-5">
              <form class="grid gap-3" onSubmit={send}>
                <TextField value={to()} onChange={setTo}>
                  <TextFieldLabel>To</TextFieldLabel>
                  <TextFieldInput type="tel" placeholder="Phone number" />
                </TextField>
                <TextField value={body()} onChange={setBody}>
                  <TextFieldLabel>Message</TextFieldLabel>
                  <TextFieldTextArea rows={3} class="resize-none" />
                </TextField>
                <Button
                  type="submit"
                  class="justify-self-end"
                  disabled={sending() || !props.device.online || !to().trim() || !body()}
                >
                  <Send /> Send
                </Button>
              </form>
            </CardContent>
          </Card>
          <Card class="gap-3 py-5">
            <CardHeader class="px-5">
              <CardTitle class="text-base">Received while you were here</CardTitle>
            </CardHeader>
            <CardContent class="grid gap-2 px-5">
              <Show
                when={incoming().length}
                fallback={<p class="text-muted-foreground text-sm">New messages appear here as they arrive.</p>}
              >
                <For each={incoming()}>
                  {(x) => (
                    <div class="rounded-lg border p-3">
                      <div class="flex justify-between gap-2 text-xs">
                        <span class="font-medium">
                          {x.m.incoming ? "From" : "To"} {x.m.contactName || x.m.address}
                        </span>
                        <span class="text-muted-foreground">{ago(x.at, now())}</span>
                      </div>
                      <p class="mt-1 text-sm break-words whitespace-pre-wrap">{x.m.body}</p>
                    </div>
                  )}
                </For>
              </Show>
            </CardContent>
          </Card>
        </div>

        <Card class="content-start gap-3 py-5">
          <CardHeader class="px-5">
            <CardTitle class="flex items-center justify-between text-base">
              Conversations
              <Button size="sm" variant="outline" onClick={load} disabled={!props.device.online}>
                <RefreshCw /> {threads() ? "Refresh" : "Load"}
              </Button>
            </CardTitle>
          </CardHeader>
          <CardContent class="px-5">
            <Show
              when={threads()}
              fallback={
                <div class="text-muted-foreground flex flex-col items-center gap-2 py-8 text-center text-sm">
                  <MessageSquare class="size-6" />
                  Load the phone's recent conversations.
                </div>
              }
            >
              <div class="divide-y">
                <For each={threads()} fallback={<p class="text-muted-foreground text-sm">No conversations.</p>}>
                  {(t) => (
                    <button
                      class="hover:bg-accent/60 flex w-full gap-3 py-2.5 text-left"
                      onClick={() => setTo(t.address)}
                    >
                      <div class="min-w-0 flex-1">
                        <div class="flex items-center gap-2">
                          <span class="truncate text-sm font-medium">{t.contactName || t.address}</span>
                          <Show when={t.unread}>
                            <span class="bg-primary text-primary-foreground rounded-full px-1.5 text-[11px]">
                              {t.unread}
                            </span>
                          </Show>
                        </div>
                        <div class="text-muted-foreground truncate text-xs">{t.snippet}</div>
                      </div>
                      <span class="text-muted-foreground shrink-0 text-xs">
                        {ago(Number(t.lastTimestampMs), now())}
                      </span>
                    </button>
                  )}
                </For>
              </div>
            </Show>
          </CardContent>
        </Card>
      </div>
    </Show>
  );
}
